import { Button, Menu, MenuHandler, MenuItem, MenuList } from '@material-tailwind/react'
import React from 'react'
import { IoChevronDownSharp } from "react-icons/io5"
import { Link, useLocation } from 'react-router-dom'
import { logout } from '../../actions/userActions'
import { useDispatch, useSelector } from 'react-redux'
import { Bars3Icon, BellIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { listNotifications, markNotificationsRead, subscribeToNotifications } from '../../utils/notificationService'

const PAGE_TITLES = [
  { match: (path) => path === '/', title: 'Dashboard' },
  { match: (path) => path.startsWith('/orders') || path.startsWith('/orderDetails'), title: 'Orders' },
  { match: (path) => path.startsWith('/create-order'), title: 'Create Order' },
  { match: (path) => path.startsWith('/bulk-order-upload'), title: 'Bulk Order Upload' },
  { match: (path) => path.startsWith('/bulk-waybill-download'), title: 'Bulk Waybill Download' },
  { match: (path) => path.startsWith('/partnerlist'), title: 'Partner List' },
  { match: (path) => path.startsWith('/manageDelivery'), title: 'Manage Delivery' },
  { match: (path) => path.startsWith('/request-pickup'), title: 'Request Pickup' },
  { match: (path) => path.startsWith('/servicibility'), title: 'Serviceability' },
  { match: (path) => path.startsWith('/warehouse'), title: 'Manage Warehouse' },
  { match: (path) => path.startsWith('/createBranch'), title: 'Create Branch' },
  { match: (path) => path.startsWith('/users'), title: 'Users' },
  { match: (path) => path.startsWith('/create-user'), title: 'Create User' },
  { match: (path) => path.startsWith('/create-staff'), title: 'Create Staff' },
  { match: (path) => path.startsWith('/profile'), title: 'Profile' },
  { match: (path) => path.startsWith('/updateProfile'), title: 'Update Profile' },
]

const getPageTitle = (pathname) => {
  const found = PAGE_TITLES.find((item) => item.match(pathname))
  return found?.title || 'GRC Logistics'
}

const Topbar = () => {
  const { userProfile, users } = useSelector((state) => state.user)
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const [notifications, setNotifications] = React.useState([])

  const currentUserId = userProfile?.id || userProfile?.uid || users || null

  const handleLogout = () => {
    dispatch(logout())
  }

  const handleOpenSidebar = () => {
    window.dispatchEvent(new Event("grc:open-sidebar"))
  }

  const displayName = userProfile?.company_name || userProfile?.name || 'Admin User'
  const initial = displayName.charAt(0).toUpperCase()
  const title = getPageTitle(pathname)
  const unreadNotifications = notifications.filter((item) => !item.read_at)

  React.useEffect(() => {
    if (!currentUserId) return undefined

    let isMounted = true

    const loadNotifications = async () => {
      const { data, error } = await listNotifications(currentUserId, 12)
      if (!isMounted || error) return
      setNotifications(data)
    }

    loadNotifications()

    const subscription = subscribeToNotifications(currentUserId, (notification) => {
      setNotifications((prev) => [notification, ...prev.filter((item) => item.id !== notification.id)].slice(0, 12))
      toast(notification.title)
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [currentUserId])

  const handleMarkAllRead = async () => {
    const unreadIds = unreadNotifications.map((item) => item.id)
    if (!unreadIds.length) return

    const { error } = await markNotificationsRead(unreadIds)
    if (error) return

    const readAt = new Date().toISOString()
    setNotifications((prev) => prev.map((item) => (
      unreadIds.includes(item.id) ? { ...item, read_at: readAt } : item
    )))
  }

  const formatNotificationTime = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className='sticky top-0 z-30 px-3 pt-3 lg:px-3'>
      <div className='crm-panel rounded-[28px] px-4 py-4 lg:px-6'>
        <div className='flex items-center justify-between gap-4'>
          <div className='flex min-w-0 items-center gap-3'>
            <Button
              variant="text"
              onClick={handleOpenSidebar}
              className='flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/80 p-0 text-slate-700 shadow-none hover:bg-white lg:hidden'
            >
              <Bars3Icon className='h-5 w-5' />
            </Button>
            <div className='min-w-0'>
              <p className='crm-kicker text-[10px] text-slate-400'>Logistics CRM</p>
              <h1 className='crm-section-title truncate text-[1.45rem] leading-none text-slate-950 lg:text-[1.8rem]'>{title}</h1>
            </div>
          </div>

          <div className='flex items-center gap-2 lg:gap-3'>
            <Menu placement="bottom-end">
              <MenuHandler>
                <Button className='relative flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200/80 bg-white/85 p-0 text-slate-700 shadow-[0_10px_24px_rgba(15,23,42,0.06)] hover:bg-white hover:shadow-[0_14px_28px_rgba(15,23,42,0.08)]'>
                  <BellIcon className='h-5 w-5' />
                  {unreadNotifications.length > 0 && (
                    <span className='absolute -right-1 -top-1 min-w-[20px] rounded-full bg-[#f97316] px-1.5 py-0.5 text-[10px] font-[GilroyBold] text-white'>
                      {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
                    </span>
                  )}
                </Button>
              </MenuHandler>
              <MenuList className='w-[360px] max-w-[calc(100vw-2rem)] p-0'>
                <div className='flex items-center justify-between border-b border-slate-100 px-4 py-3'>
                  <div>
                    <p className='text-sm font-[GilroyBold] text-slate-900'>Notifications</p>
                    <p className='text-xs text-slate-400'>{unreadNotifications.length} unread</p>
                  </div>
                  <button
                    type='button'
                    onClick={handleMarkAllRead}
                    className='text-xs font-[GilroyBold] text-[#f97316]'
                  >
                    Mark all read
                  </button>
                </div>
                <div className='max-h-[420px] overflow-y-auto'>
                  {notifications.length ? notifications.map((item) => (
                    <div
                      key={item.id}
                      className={`border-b border-slate-100 px-4 py-3 last:border-b-0 ${
                        item.read_at ? 'bg-white' : 'bg-[#fffaf6]'
                      }`}
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='min-w-0'>
                          <p className='text-sm font-[GilroyBold] text-slate-900'>{item.title}</p>
                          <p className='mt-1 text-xs leading-5 text-slate-500'>{item.message}</p>
                          {item.order_reference && (
                            <p className='mt-2 text-[11px] font-[GilroyBold] uppercase tracking-[0.12em] text-[#f97316]'>
                              {item.order_reference}
                            </p>
                          )}
                        </div>
                        {!item.read_at && <span className='mt-1 h-2.5 w-2.5 rounded-full bg-[#f97316]' />}
                      </div>
                      <p className='mt-2 text-[11px] text-slate-400'>{formatNotificationTime(item.created_at)}</p>
                    </div>
                  )) : (
                    <div className='px-4 py-10 text-center text-sm text-slate-500'>
                      No notifications yet
                    </div>
                  )}
                </div>
              </MenuList>
            </Menu>

            <Menu placement="bottom-end">
              <MenuHandler>
                <Button className="flex items-center gap-3 rounded-full border border-slate-200/80 bg-white/85 px-2 py-1.5 text-slate-900 shadow-[0_10px_24px_rgba(15,23,42,0.06)] hover:bg-white hover:shadow-[0_14px_28px_rgba(15,23,42,0.08)]">
                  <div className='flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#fb923c] via-[#f97316] to-[#0f172a] text-sm font-[GilroyBold] text-white'>
                    {initial}
                  </div>
                  <div className='hidden min-w-0 text-left lg:block'>
                    <p className='truncate text-sm font-[GilroyBold] text-slate-800'>{displayName}</p>
                    <p className='truncate text-[11px] text-slate-400'>{userProfile?.role || 'User'}</p>
                  </div>
                  <IoChevronDownSharp className='hidden text-slate-500 lg:block' />
                </Button>
              </MenuHandler>
              <MenuList>
                <Link to="/profile"><MenuItem>Profile</MenuItem></Link>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </MenuList>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Topbar
